import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { CheckCircle2, Key, Loader2, Save, X, XCircle } from 'lucide-react'
import { useAppDispatch } from '@/redux/hooks'
import { getRoomDetails, updateRoomDetails } from '@/redux/asyncThunks/room'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { toast } from 'sonner'
import { GameRoomStatus } from '@/redux/types/room'
import { validateLLMApiKeysAPI } from '@/api/room'

type KeyValidationState = 'idle' | 'validating' | 'valid' | 'invalid'

interface LLMApiKeysSectionProps {
  roomId: string
  disabled?: boolean
}

export default function LLMApiKeysSection({
  roomId,
  disabled = false,
}: LLMApiKeysSectionProps) {
  const dispatch = useAppDispatch()
  const { currentRoom } = useSelector((state: RootState) => state.room)
  const [openaiKey, setOpenaiKey] = useState<string>('')
  const [claudeKey, setClaudeKey] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const [openaiValidation, setOpenaiValidation] = useState<KeyValidationState>('idle')
  const [claudeValidation, setClaudeValidation] = useState<KeyValidationState>('idle')
  const [openaiError, setOpenaiError] = useState<string>('')
  const [claudeError, setClaudeError] = useState<string>('')

  const isCurrentRoomLoaded = currentRoom?.roomid === roomId
  const openaiConfigured = currentRoom?.openai_api_key_configured ?? false
  const claudeConfigured = currentRoom?.claude_api_key_configured ?? false
  const hasGameSpecificKeys = openaiConfigured && claudeConfigured
  const isGameInProcess =
    currentRoom?.status === GameRoomStatus.IN_PROGRESS ||
    currentRoom?.status === GameRoomStatus.PAUSED
  const isKeysLocked = currentRoom?.status !== GameRoomStatus.NOT_STARTED
  const isValidating = openaiValidation === 'validating' || claudeValidation === 'validating'
  const isDisabled = disabled || isKeysLocked || isSaving || isValidating

  const hasOpenaiChange = openaiKey !== ''
  const hasClaudeChange = claudeKey !== ''
  const hasChanges = hasOpenaiChange || hasClaudeChange
  const effectiveOpenaiSet = hasOpenaiChange ? !!openaiKey.trim() : openaiConfigured
  const effectiveClaudeSet = hasClaudeChange ? !!claudeKey.trim() : claudeConfigured
  const canSave = hasChanges && effectiveOpenaiSet && effectiveClaudeSet

  const handleOpenaiChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setOpenaiKey(e.target.value)
    if (openaiValidation !== 'idle') {
      setOpenaiValidation('idle')
      setOpenaiError('')
    }
  }, [openaiValidation])

  const handleClaudeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setClaudeKey(e.target.value)
    if (claudeValidation !== 'idle') {
      setClaudeValidation('idle')
      setClaudeError('')
    }
  }, [claudeValidation])

  const handleSave = useCallback(async () => {
    if (!hasChanges || !canSave) return

    const keysToValidate: { openai_api_key?: string; claude_api_key?: string } = {}
    if (hasOpenaiChange && openaiKey.trim()) keysToValidate.openai_api_key = openaiKey.trim()
    if (hasClaudeChange && claudeKey.trim()) keysToValidate.claude_api_key = claudeKey.trim()

    if (keysToValidate.openai_api_key) setOpenaiValidation('validating')
    if (keysToValidate.claude_api_key) setClaudeValidation('validating')

    let validationFailed = false
    if (keysToValidate.openai_api_key || keysToValidate.claude_api_key) {
      try {
        const results = await validateLLMApiKeysAPI(roomId, keysToValidate)

        if (results.openai !== undefined) {
          if (results.openai.valid === false) {
            setOpenaiValidation('invalid')
            setOpenaiError(results.openai.error ?? 'Invalid API key — please check and re-enter.')
            validationFailed = true
          } else if (results.openai.valid === null) {
            setOpenaiValidation('idle')
            toast.warning(results.openai.error ?? 'OpenAI key could not be verified — saving anyway.')
          } else {
            setOpenaiValidation('valid')
          }
        }

        if (results.claude !== undefined) {
          if (results.claude.valid === false) {
            setClaudeValidation('invalid')
            setClaudeError(results.claude.error ?? 'Invalid API key — please check and re-enter.')
            validationFailed = true
          } else if (results.claude.valid === null) {
            setClaudeValidation('idle')
            toast.warning(results.claude.error ?? 'Claude key could not be verified — saving anyway.')
          } else {
            setClaudeValidation('valid')
          }
        }
      } catch {
        if (keysToValidate.openai_api_key) setOpenaiValidation('idle')
        if (keysToValidate.claude_api_key) setClaudeValidation('idle')
        toast.warning('Could not validate API keys — saving without verification.')
      }
    }

    if (validationFailed) return

    setIsSaving(true)
    try {
      const payload: {
        roomId: string
        openai_api_key?: string | null
        claude_api_key?: string | null
      } = { roomId }
      if (hasOpenaiChange) payload.openai_api_key = openaiKey.trim() || null
      if (hasClaudeChange) payload.claude_api_key = claudeKey.trim() || null
      await dispatch(updateRoomDetails(payload)).unwrap()
      toast.success('LLM API keys updated successfully')
      setOpenaiKey('')
      setClaudeKey('')
      setOpenaiValidation('idle')
      setClaudeValidation('idle')
      dispatch(getRoomDetails(roomId))
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to update API keys'
      )
    } finally {
      setIsSaving(false)
    }
  }, [roomId, openaiKey, claudeKey, hasOpenaiChange, hasClaudeChange, hasChanges, canSave, dispatch])

  const handleClearBothKeys = useCallback(async () => {
    setIsSaving(true)
    try {
      await dispatch(
        updateRoomDetails({
          roomId,
          openai_api_key: null,
          claude_api_key: null,
        })
      ).unwrap()
      toast.success('Both API keys cleared')
      setOpenaiKey('')
      setClaudeKey('')
      setOpenaiValidation('idle')
      setClaudeValidation('idle')
      dispatch(getRoomDetails(roomId))
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to clear API keys'
      )
    } finally {
      setIsSaving(false)
    }
  }, [roomId, dispatch])

  if (!isCurrentRoomLoaded) {
    return(
      <div>
        <Loader2 className='w-4 h-4 animate-spin' />
        Loading game details...
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2 text-lg'>
          <Key className='h-5 w-5' />
          LLM API keys (optional)
        </CardTitle>
        <CardDescription>
          Both OpenAI and Claude API keys are required for this game. Set
          per-game keys below; all LLM usage will use these keys. Leave blank
          to use the default server keys.
          Keys are never displayed after saving.
        </CardDescription>
        <div className='rounded-md border bg-muted/40 px-3 py-2'>
          <p className='text-sm font-medium text-foreground'>
            Current key source:{' '}
            <span
              className={
                hasGameSpecificKeys ? 'text-emerald-700' : 'text-blue-700'
              }
            >
              {hasGameSpecificKeys
                ? 'Using user-defined API keys'
                : 'Using default server API keys'}
            </span>
          </p>
        </div>
        {isGameInProcess && (
          <p className='text-sm rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900'>
            This game is currently in progress. API keys are locked until the
            game ends.
          </p>
        )}
        {isKeysLocked && !isGameInProcess && (
          <p className='text-sm text-muted-foreground'>
            API keys are read-only because this game has already started.
          </p>
        )}
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='openai-api-key'>OpenAI API key</Label>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-muted-foreground text-sm shrink-0 w-24'>
              {openaiConfigured ? 'Configured' : 'Not set'}
            </span>
            <div className='relative flex items-center max-w-md w-full'>
              <Input
                id='openai-api-key'
                type='text'
                placeholder={openaiConfigured ? 'Enter new key to replace' : 'sk-...'}
                value={openaiKey}
                onChange={handleOpenaiChange}
                disabled={isDisabled}
                className={`pr-8 font-mono text-sm ${openaiValidation === 'invalid' ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                autoComplete='off'
                spellCheck={false}
              />
              {openaiValidation === 'validating' && (
                <Loader2 className='absolute right-2 h-4 w-4 animate-spin text-muted-foreground' />
              )}
              {openaiValidation === 'valid' && (
                <CheckCircle2 className='absolute right-2 h-4 w-4 text-green-600' />
              )}
              {(openaiValidation === 'invalid' || (openaiValidation === 'idle' && openaiKey)) && (
                <button
                  type='button'
                  onClick={() => { setOpenaiKey(''); setOpenaiValidation('idle'); setOpenaiError('') }}
                  disabled={isDisabled}
                  className='absolute right-2 h-4 w-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-50'
                  aria-label='Clear OpenAI API key'
                >
                  <XCircle className={`h-4 w-4 ${openaiValidation === 'invalid' ? 'text-red-500' : ''}`} />
                </button>
              )}
            </div>
          </div>
          {openaiValidation === 'invalid' && openaiError && (
            <p className='text-sm text-red-600 ml-[6.5rem]'>{openaiError}</p>
          )}
        </div>
        <div className='space-y-2'>
          <Label htmlFor='claude-api-key'>Claude (Anthropic) API key</Label>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-muted-foreground text-sm shrink-0 w-24'>
              {claudeConfigured ? 'Configured' : 'Not set'}
            </span>
            <div className='relative flex items-center max-w-md w-full'>
              <Input
                id='claude-api-key'
                type='text'
                placeholder={
                  claudeConfigured ? 'Enter new key to replace' : 'sk-ant-...'
                }
                value={claudeKey}
                onChange={handleClaudeChange}
                disabled={isDisabled}
                className={`pr-8 font-mono text-sm ${claudeValidation === 'invalid' ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                autoComplete='off'
                spellCheck={false}
              />
              {claudeValidation === 'validating' && (
                <Loader2 className='absolute right-2 h-4 w-4 animate-spin text-muted-foreground' />
              )}
              {claudeValidation === 'valid' && (
                <CheckCircle2 className='absolute right-2 h-4 w-4 text-green-600' />
              )}
              {(claudeValidation === 'invalid' || (claudeValidation === 'idle' && claudeKey)) && (
                <button
                  type='button'
                  onClick={() => { setClaudeKey(''); setClaudeValidation('idle'); setClaudeError('') }}
                  disabled={isDisabled}
                  className='absolute right-2 h-4 w-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-50'
                  aria-label='Clear Claude API key'
                >
                  <XCircle className={`h-4 w-4 ${claudeValidation === 'invalid' ? 'text-red-500' : ''}`} />
                </button>
              )}
            </div>
          </div>
          {claudeValidation === 'invalid' && claudeError && (
            <p className='text-sm text-red-600 ml-[6.5rem]'>{claudeError}</p>
          )}
        </div>
        <p className='text-sm text-muted-foreground'>
          Both keys are required.
        </p>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            type='button'
            onClick={handleSave}
            disabled={!canSave || isDisabled}
            className='bg-green-600 hover:bg-green-700'
          >
            {isValidating ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Validating...
              </>
            ) : (
              <>
                <Save className='h-4 w-4 mr-2' />
                {isSaving ? 'Saving...' : 'Save API keys'}
              </>
            )}
          </Button>
          {(openaiConfigured && claudeConfigured) && (
            <Button
              type='button'
              variant='outline'
              disabled={isDisabled}
              onClick={handleClearBothKeys}
            >
              <X className='h-4 w-4 mr-2' />
              Clear both keys
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
